import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { MammothMascot } from "../components/MammothMascot";
import { useAuth } from "../context/AuthContext";
import { levelFor } from "../lib/levels";
import { Send } from "lucide-react";

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const lvl = levelFor(user?.current_level || 1);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/coach/history").then(r => setMessages(r.data.items)).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setBusy(true);
    setMessages(m => [...m, { id: "tmp", role: "user", text, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post("/coach/chat", { message: text });
      // refresh full history (includes the user msg saved server-side)
      const hist = await api.get("/coach/history");
      setMessages(hist.data.items);
    } catch {
      setMessages(m => [...m, { id: "err", role: "assistant", text: "I'm here, but something snagged. Try again?" }]);
    }
    setBusy(false);
  };

  return (
    <div className="animate-fadeUp" data-testid="coach-page">
      <div className="text-xs font-bold uppercase tracking-[0.25em] opacity-60">Coach</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mt-2">Talk to your mammoth</h1>
      <p className="opacity-70 mt-2">A wise, calm coach. Neuroscience-powered. Not therapy — just a friend with deep ears.</p>

      <div className="mt-8 rounded-3xl p-4 sm:p-6 border border-[#0e203b]/5 shadow-lg" style={{ backgroundColor: lvl.surface }}>
        <div className="h-[55vh] overflow-y-auto space-y-4 px-2" data-testid="coach-messages">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center opacity-70">
              <MammothMascot size="clamp(110px, 18vw, 170px)" stage={user?.current_level} shape="rounded" />
              <div className="mt-4 font-display font-bold text-xl">Tell me what's loud today.</div>
              <div className="text-sm">Anything — one word is enough.</div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.id || i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && (
                <MammothMascot size={40} stage={user?.current_level} shape="circle" className="flex-shrink-0" />
              )}
              <div data-testid={`msg-${m.role}`} className={`max-w-[75%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-[#0e203b] text-white" : "bg-white border border-[#0e203b]/5"}`}>
                <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex gap-3">
              <MammothMascot size={40} stage={user?.current_level} shape="circle" className="flex-shrink-0" />
              <div className="bg-white rounded-2xl px-4 py-3 border border-[#0e203b]/5 text-sm opacity-60">thinking…</div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="mt-4 flex gap-2">
          <input
            data-testid="coach-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Say it freely…"
            className="flex-1 h-12 px-5 rounded-full border-2 border-[#0e203b]/10 focus:border-[#0e203b] outline-none font-medium bg-white"
          />
          <button data-testid="coach-send" disabled={busy || !input.trim()} className="btn-pill bg-[#0e203b] text-white rounded-full px-5 h-12 font-bold disabled:opacity-40 flex items-center gap-2">
            <Send size={16} /> Send
          </button>
        </form>
      </div>
    </div>
  );
}
